"use client";

// Client auth context for the dashboard shell.
// Guards the (app) routes: if there is no token, redirect to /login.
// Exposes the current identity + logout for the sidebar/topbar.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useRouter } from "next/navigation";
import { authApi, type AuthUser } from "../lib/api";
import { clearSession, getStoredUser, getToken, setStoredUser } from "../lib/auth";
import { teardownPushOnLogout } from "../lib/pushTeardown";

interface AuthCtx {
  user: AuthUser | null;
  ready: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    try {
      const env = await authApi.profile();
      if (env.data) {
        const u: AuthUser = {
          id: env.data.id,
          firstName: env.data.firstName,
          lastName: env.data.lastName,
          email: env.data.email,
          role: env.data.role,
          profileImage: env.data.profileImage,
        };
        setUser(u);
        setStoredUser(u, true);
      }
    } catch {
      // 401 already clears the session inside the client; bounce to login.
    }
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      router.replace("/login");
      setReady(true);
      return;
    }
    setUser(getStoredUser());
    setReady(true);
    // Revalidate identity against the server in the background.
    void refresh();
  }, [router, refresh]);

  const logout = useCallback(async () => {
    // Unregister the push token first: the endpoint is authenticated, so it must
    // run while the JWT is still in storage. Never throws (lib/pushTeardown).
    await teardownPushOnLogout();

    try {
      // Audited server-side; the client session is dropped either way.
      await authApi.logout();
    } catch {
      // Network/401 — signing out locally must still succeed.
    }
    clearSession();
    setUser(null);
    router.replace("/login");
  }, [router]);

  // Don't paint protected content (or let children fire API calls) until the
  // token check has run. If there's no token we're mid-redirect to /login.
  if (!ready) return null;
  if (!getToken()) return null;

  return (
    <Ctx.Provider value={{ user, ready, logout, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
